/**
 * What kind of thing a citation quotes, as the API names it (#5986).
 *
 * A citation is named after its canonical record, so "Fee Schedule" can be a file, a proposal or
 * an article; this says which. The API sends it by name. A name this build does not know is read
 * as no kind, so a newer API cannot fail an answer by adding one.
 */
export enum CitationSourceKind {
  File = "File",
  ForumMessage = "ForumMessage",
  Proposal = "Proposal",
  WorkAgreement = "WorkAgreement",
  KnowledgeBaseArticle = "KnowledgeBaseArticle",
  MarketplaceProfile = "MarketplaceProfile",
  /** A screen of the application, from the sitemap the assistant answers "where do I ..." from. */
  AppPage = "AppPage",
}

/** The short label the citation row shows beside the kind's icon. */
export const CITATION_SOURCE_KIND_LABELS: Readonly<Record<CitationSourceKind, string>> = {
  [CitationSourceKind.File]: "File",
  [CitationSourceKind.ForumMessage]: "Forum message",
  [CitationSourceKind.Proposal]: "Proposal",
  [CitationSourceKind.WorkAgreement]: "Work agreement",
  [CitationSourceKind.KnowledgeBaseArticle]: "Knowledge base article",
  [CitationSourceKind.MarketplaceProfile]: "Marketplace profile",
  [CitationSourceKind.AppPage]: "App page",
};

/** What a citation's `locator` names inside its source (#6019). */
export enum CitationLocatorKind {
  /** The heading of the knowledge base article section the text is under. */
  Section = "Section",
  /** The spreadsheet sheet the text is on. */
  Sheet = "Sheet",
  /** The title of the slide the text is on; the slide's number is the citation's page. */
  Slide = "Slide",
}

/** How a cited span's text was read from its file (#6019). */
export enum CitationTextSource {
  TextLayer = "TextLayer",
  /** Recognised from page images by OCR. */
  Ocr = "Ocr",
  /** From pages of which some were recognised by OCR. */
  Mixed = "Mixed",
}

/** `value` as a member of `members`, or undefined for none or a name this build does not know. */
export function knownMember<T extends string>(
  members: Readonly<Record<string, T>>,
  value: string | null | undefined,
): T | undefined {
  return Object.values(members).find(member => member === value);
}
